import { isInAppPathShape } from "@frontend-shared/utils/in-app-path";

// NOTE: Defense-in-depth wrapper for `globalThis.location.href = ...` calls
// where the URL came from an API response or an attacker-reachable query
// param. Without this, a compromised backend, a man-in-the-middle on the API
// channel or a crafted link could redirect users to a phishing page or trigger
// a `javascript:` execution. The check is intentionally narrow:
//   - in-app paths are safe only when they resolve to the current origin
//     (see `toSafeSameOriginPath`)
//   - absolute http(s) URLs are safe (`isSafeExternalRedirect` only)
//   - everything else (javascript:, data:, file:, vbscript:, blank, …)
//     is treated as a redirect attempt to be refused
//
// This does NOT enforce a host allowlist for absolute URLs — for SAML/OAuth
// flows the IdP host is arbitrary by design. Hard-blocking dangerous schemes
// is the realistic floor; deeper allowlisting belongs at the backend.

const ALLOWED_SCHEMES: ReadonlySet<string> = new Set(["http:", "https:"]);

interface IResolveSameOriginPathProps {
  candidate: unknown;
  origin?: string;
}

// NOTE: Declared flat rather than extending IResolveSameOriginPathProps so the
// optional `origin` stays last.
export interface IToSafeSameOriginPathProps {
  candidate: unknown;
  fallback: string;
  origin?: string;
}

const readCurrentOrigin = (): string | null =>
  typeof globalThis.location === "undefined"
    ? null
    : globalThis.location.origin;

// NOTE: NSAI-5409 — the shape check alone is a prefix check, and prefix checks
// are what the "/\evil.example" bypass beat. The candidate is therefore also
// resolved against the origin with the same parser the browser uses for the
// navigation, and kept only if it stays there.
//
// The serialized result is resolved a second time on purpose: dot segments
// collapse during parsing, so "/.//evil.example" resolves same-origin but
// serializes to the pathname "//evil.example" — protocol-relative, i.e.
// off-origin the moment it is assigned to `location.href`.
const resolveSameOriginPath = ({
  candidate,
  origin,
}: IResolveSameOriginPathProps): string | null => {
  if (!isInAppPathShape(candidate)) return null;

  const baseOrigin = origin ?? readCurrentOrigin();

  if (!baseOrigin) return null;

  try {
    const base = new URL(baseOrigin);
    const resolved = new URL(candidate, base);

    if (resolved.origin !== base.origin) return null;

    const path = `${resolved.pathname}${resolved.search}${resolved.hash}`;

    return isInAppPathShape(path) && new URL(path, base).origin === base.origin
      ? path
      : null;
  } catch {
    return null;
  }
};

// NOTE: The reusable guard for any redirect target that must stay in the app
// (a `redirectTo` round-tripped through OAuth state, a path remembered in
// storage). Returns the normalized same-origin path, or `fallback` when the
// candidate is missing, not a string, not an in-app path or resolves to any
// other origin. `origin` defaults to the current document's origin and exists
// so the guard can be exercised outside a browser.
export const toSafeSameOriginPath = ({
  candidate,
  fallback,
  origin,
}: IToSafeSameOriginPathProps): string =>
  resolveSameOriginPath({ candidate, origin }) ?? fallback;

const resolveSafeRedirectTarget = (raw: string): string | null => {
  if (typeof raw !== "string" || raw.length === 0) return null;
  if (raw.startsWith("/") || raw.startsWith("\\")) {
    return resolveSameOriginPath({ candidate: raw });
  }

  try {
    const parsed = new URL(raw);

    return ALLOWED_SCHEMES.has(parsed.protocol) ? parsed.href : null;
  } catch {
    return null;
  }
};

export const isSafeExternalRedirect = (raw: string): boolean =>
  resolveSafeRedirectTarget(raw) !== null;

// NOTE: Throws when the target is unsafe so the caller's error handling
// (toast / fallback redirect) can surface the failure to the user, instead
// of silently dropping the navigation. Assigns the normalized target, never
// the raw string, so what was validated is exactly what is navigated to.
export const assignSafeLocation = (raw: string): void => {
  const target = resolveSafeRedirectTarget(raw);

  if (target === null) {
    throw new Error(
      `Refusing to redirect to unsafe target: ${raw.slice(0, 256)}`,
    );
  }

  globalThis.location.href = target;
};
